// =============================================================================
// Variant Selection Step — Shopify-style color swatches, size pills, preset cards
// =============================================================================

import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';
import { DYNAMIC_OPTION_PRODUCT_TYPES } from '@tkpod/shared';
import { OptionSelector } from '@/components/catalog/OptionSelector';
import type { SelectedOptionEntry } from '@/types/order-wizard';

const CUID_RE = /^c[a-z0-9]{24,}$/;
const safeParse = (v: string | null | undefined): number => {
  const n = parseFloat(v ?? '');
  return Number.isFinite(n) ? n : 0;
};

interface Preset {
  id: string;
  name: string;
  sku: string | null;
  width: number;
  height: number;
  basePrice: string;
}

interface OptionValue {
  id: string;
  name: string;
  slug: string;
  displayName: string;
  metadata: Record<string, unknown>;
  swatchUrl: string | null;
  mockupImageUrl: string | null;
  surcharge: string | null;
  sortOrder?: number;
}

interface ProductOption {
  optionTypeName: string;
  optionTypeSlug: string;
  uiType: string;
  position: number;
  values: OptionValue[];
}

interface ProductVariant {
  id: string;
  sku: string;
  cost: string;
  weightOz: number | null;
  isActive: boolean;
  options: Record<string, string>;
}

interface BlankProduct {
  id: string;
  brand: string;
  model: string;
  name: string;
  isPrimary: boolean;
  variants: ProductVariant[];
}

interface TemplateDetail {
  id: string;
  name: string;
  type: string;
  presets: Preset[];
  options: ProductOption[];
  blankProducts: BlankProduct[];
  pricingProfile: {
    strategy: string;
    baseFee: string | null;
  } | null;
}

interface VariantSelectionStepProps {
  uptId: string;
  uptType: string;
  selectedPresetId: string | null;
  onSelectPreset: (preset: Preset) => void;
  onVariantResolved: (variantId: string | null, price: number, options: SelectedOptionEntry[]) => void;
  preSelectedVariantId?: string | null;
  preSelectedOptions?: Record<string, string> | null;
}


export function VariantSelectionStep({
  uptId,
  uptType,
  selectedPresetId,
  onSelectPreset,
  onVariantResolved,
  preSelectedVariantId,
  preSelectedOptions,
}: VariantSelectionStepProps) {
  const api = useApiClient();
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);
  const onVariantResolvedRef = useRef(onVariantResolved);
  onVariantResolvedRef.current = onVariantResolved;

  const detailQuery = useQuery({
    queryKey: ['catalog', 'templates', uptId],
    queryFn: () =>
      api.get<{ data: TemplateDetail }>(
        `/api/v1/catalog/templates/${uptId}`,
      ),
    enabled: !!uptId && CUID_RE.test(uptId),
  });

  const template = detailQuery.data?.data;
  const useDynamicOptions = DYNAMIC_OPTION_PRODUCT_TYPES.has(uptType) && (template?.options?.length ?? 0) > 0;

  useEffect(() => {
    if (initialized || !template) return;

    if (preSelectedOptions && useDynamicOptions) {
      setSelections(preSelectedOptions);
      setInitialized(true);
    } else if (preSelectedVariantId && useDynamicOptions) {
      const allVariants = template.blankProducts.flatMap((bp) => bp.variants);
      const variant = allVariants.find((v) => v.id === preSelectedVariantId);
      if (variant) {
        const newSelections: Record<string, string> = {};
        for (const opt of template.options) {
          const valueSlug = variant.options[opt.optionTypeSlug];
          const matchedVal = opt.values.find((v) => v.slug === valueSlug);
          if (matchedVal) {
            newSelections[opt.optionTypeSlug] = matchedVal.id;
          }
        }
        setSelections(newSelections);
      }
      setInitialized(true);
    } else {
      setInitialized(true);
    }
  }, [template, initialized, preSelectedOptions, preSelectedVariantId, useDynamicOptions]);

  const allVariants = useMemo(() => {
    if (!template) return [];
    return template.blankProducts.flatMap((bp) => bp.variants);
  }, [template]);

  const resolvedVariant = useMemo(() => {
    if (!template || !useDynamicOptions) return null;
    const optionSlugs = template.options.map((o) => o.optionTypeSlug);
    const allSelected = optionSlugs.every((s) => selections[s]);
    if (!allSelected) return null;

    return (
      allVariants.find((v) => {
        if (!v.isActive) return false;
        return optionSlugs.every((os) => {
          const selectedValueId = selections[os];
          const selectedVal = template.options
            .find((o) => o.optionTypeSlug === os)
            ?.values.find((val) => val.id === selectedValueId);
          if (!selectedVal) return false;
          return v.options[os] === selectedVal.slug;
        });
      }) ?? null
    );
  }, [template, selections, allVariants, useDynamicOptions]);

  const computedPrice = useMemo(() => {
    if (!template || !useDynamicOptions || !resolvedVariant) return null;

    let cents = Math.round(safeParse(resolvedVariant.cost) * 100);
    for (const opt of template.options) {
      const selectedId = selections[opt.optionTypeSlug];
      const val = opt.values.find((v) => v.id === selectedId);
      if (val?.surcharge) {
        cents += Math.round(safeParse(val.surcharge) * 100);
      }
    }
    return cents / 100;
  }, [template, resolvedVariant, selections, useDynamicOptions]);

  const allOptionsSelected = useMemo(() => {
    if (!template || !useDynamicOptions) return false;
    return template.options.every((o) => selections[o.optionTypeSlug]);
  }, [template, selections, useDynamicOptions]);

  useEffect(() => {
    if (!useDynamicOptions || !template) return;

    const optionEntries: SelectedOptionEntry[] = template.options
      .filter((o) => selections[o.optionTypeSlug])
      .map((o) => {
        const val = o.values.find((v) => v.id === selections[o.optionTypeSlug]);
        return {
          typeSlug: o.optionTypeSlug,
          typeName: o.optionTypeName,
          valueId: selections[o.optionTypeSlug],
          valueName: val?.displayName || val?.name || '',
          surcharge: safeParse(val?.surcharge),
        };
      });

    onVariantResolvedRef.current(
      resolvedVariant?.id ?? null,
      computedPrice ?? 0,
      optionEntries,
    );
  }, [resolvedVariant, computedPrice, useDynamicOptions, template, selections]);

  const handleOptionChange = (typeSlug: string, valueId: string) => {
    setSelections((prev) => ({ ...prev, [typeSlug]: valueId }));
  };

  if (detailQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-error-600">
          Failed to load template. Please try again.
        </p>
        <button
          type="button"
          className="mt-3 text-sm font-medium text-brand-600 hover:text-brand-700"
          onClick={() => detailQuery.refetch()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="py-20 text-center text-sm text-text-tertiary">
        Template not found.
      </div>
    );
  }

  if (useDynamicOptions) {
    return (
      <div className="space-y-8">
        {/* Dynamic Option Selectors */}
        {template.options
          .sort((a, b) => a.position - b.position)
          .map((opt) => (
            <OptionSelector
              key={opt.optionTypeSlug}
              type={opt.optionTypeName}
              uiType={opt.uiType}
              values={opt.values}
              selectedId={selections[opt.optionTypeSlug] ?? null}
              onChange={(valueId) => handleOptionChange(opt.optionTypeSlug, valueId)}
            />
          ))}

        {/* Price & Availability */}
        {allOptionsSelected && (
          <div>
            {resolvedVariant ? (
              <div className="rounded-xl border border-brand-200 bg-brand-50/50 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-text-secondary">Unit Price</p>
                    <p className="mt-0.5 text-xs text-text-tertiary">
                      SKU: {resolvedVariant.sku}
                    </p>
                    {resolvedVariant.weightOz != null && resolvedVariant.weightOz > 0 && (
                      <p className="text-sm text-text-secondary">
                        Weight: {resolvedVariant.weightOz} oz
                      </p>
                    )}
                  </div>
                  {computedPrice !== null ? (
                    <span className="text-2xl font-bold text-text-primary">
                      ${computedPrice.toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-sm text-text-tertiary">
                      Pricing not available
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-warning-200 bg-warning-50/50 p-4">
                <p className="text-sm text-warning-700">
                  This combination is not available. Please select a different option.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Preset-based products (DTF, Gangsheet, UV Sticker, etc.)
  return (
    <div>
      <div className="mb-5">
        <h3 className="text-[15px] font-semibold text-text-primary">
          Select Size
        </h3>
        <p className="mt-1 text-sm text-text-secondary">
          Choose a size preset for your transfer
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(template.presets ?? []).map((preset) => {
          const isSelected = preset.id === selectedPresetId;
          return (
            <button
              key={preset.id}
              className={`
                group relative w-full rounded-xl border-2 p-5 text-left
                transition-all duration-150
                ${
                  isSelected
                    ? 'border-brand-600 bg-brand-50 shadow-sm'
                    : 'border-border bg-white hover:border-brand-300 hover:shadow-sm'
                }
              `}
              onClick={() => onSelectPreset(preset)}
            >
              {isSelected && (
                <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600">
                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              )}

              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-surface-tertiary">
                  <svg className="h-5 w-5 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-text-primary">
                    {preset.name}
                  </h4>
                  <p className="mt-0.5 text-xs text-text-secondary">
                    {preset.width}&quot; &times; {preset.height}&quot;
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-text-primary">
                    ${safeParse(preset.basePrice).toFixed(2)}
                  </span>
                  <p className="text-[11px] text-text-tertiary">per unit</p>
                </div>
              </div>

              {preset.sku && (
                <p className="mt-2 text-[11px] text-text-tertiary pl-15">
                  SKU: {preset.sku}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
